import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import api from "../services/api";
import { normalizePlan } from "../services/apiUtils";

const PlanContext = createContext();

export function PlanProvider({ children }) {
    const [planoStatus, setPlanoStatus] = useState(null);
    const [restantes, setRestantes] = useState(0);
    const [loadingPlano, setLoadingPlano] = useState(false);
    const [clienteId, setClienteId] = useState(null);
    const [imgUtilizadas, setImgUtilizadas] = useState(null);
    const [imagensRestantes, setImagensRestantes] = useState(null);
    const eventSourceRef = useRef(null);

    // Guarda o tipo do plano (PROFESSOR | MENTOR | MESTRE)
    const [planoTipo, setPlanoTipo] = useState(null);

    //const monthRef = (d = new Date()) => d.toISOString().slice(0, 7); // "YYYY-MM"

    function getQuotaByPlan(planStr = "") {
        const p = String(planStr || "").toUpperCase().trim();
        switch (p) {
            case "PROFESSOR": return 10;
            case "MENTOR": return 25;
            case "MESTRE": return 50;
            default: return 0;
        }
    }

    function getImageQuotaByPlan(planStr = "") {
        const p = String(planStr || "").toUpperCase().trim();
        switch (p) {
            case "MENTOR": return 10;
            case "MESTRE": return 20;
            default: return 0;
        }
    }

    const fetchStatusPlano = useCallback(async (userId) => {
        if (!userId) return;
        setLoadingPlano(true);
        try {
            const res = await api.get('/billing/plan');
            const planData = normalizePlan(res.data);

            setImgUtilizadas(planData.usedImages);

            const rawStatus = planData?.status || null;
            const status = rawStatus;
            setPlanoStatus(status);

            // guarda tipo de plano (pode ser null quando trial)
            // Parceiro (B) usa as mesmas cotas efetivas do plano MENTOR.
            const tipoPlano = status === "B" ? "MENTOR" : (planData?.plan ?? null);
            setPlanoTipo(tipoPlano || null);

            // ASSINANTE (A) ou PARCEIRO (B): quota depende do plano efetivo
            if (["A", "B"].includes(status)) {
                const quota = Number(planData?.documentQuota || 0) || getQuotaByPlan(tipoPlano);
                const curr = new Date().toISOString().slice(0, 7);
                const mesRef = planData?.monthReference || null;
                const usados = (mesRef === curr) ? Number(planData?.usedGeneration || 0) : 0;
                const restantesCalc = Math.max(quota - usados, 0);
                const imageQuota = Number(planData?.imageQuota || 0) || getImageQuotaByPlan(tipoPlano);
                const imagensUsadas = (mesRef === curr) ? Number(planData?.usedImages || 0) : 0;
                const saldoExtraImagens = Number(planData?.extraImageBalance || 0);
                const imagensRestantesCalc = Math.max(imageQuota + saldoExtraImagens - imagensUsadas, 0);
                setRestantes(restantesCalc);
                setImagensRestantes(imagensRestantesCalc);

                if (planData?.externalCustomerId) setClienteId(planData.externalCustomerId);
                setLoadingPlano(false);
                return;
            }

            // TRIAL (G)
            if (status === "G") {
                const usadas = Number(planData?.usedGeneration ?? 0);
                const trialLimit = Number(planData?.trialLimit) || 1;

                if (usadas >= trialLimit) {
                    setPlanoStatus("G0");
                    setRestantes(0);
                    setImagensRestantes(0);
                } else {
                    setPlanoStatus("G");
                    setRestantes(trialLimit - usadas);
                    setImagensRestantes(0);
                }

                if (planData?.externalCustomerId) setClienteId(planData.externalCustomerId);
                setLoadingPlano(false);
                return;
            }

            // P/C ou indefinidos
            if (planData?.externalCustomerId) setClienteId(planData.externalCustomerId);
            setRestantes(0);
            setImagensRestantes(0);
        } catch (e) {
            setPlanoStatus(null);
            setRestantes(0);
            setImagensRestantes(null);
            setPlanoTipo(null);
        }
        setLoadingPlano(false);
    }, []);

    // Conecta/desconecta do SSE sempre que o externalCustomerId mudar
    useEffect(() => {
        if (!clienteId) return;

        // Fecha o EventSource anterior se existir
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const url = `${process.env.REACT_APP_API_URL || ""}/billing/subscription/events/${clienteId}`;
        const eventSource = new window.EventSource(url);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.status) {
                    setPlanoStatus(data.status);

                    // 👉 Se o servidor enviar 'restantes' no evento, atualize.
                    if (typeof data.remaining === "number" || typeof data.restantes === "number") {
                        setRestantes(data.remaining ?? data.restantes);
                    } else {
                        // 👉 Não zere 'A' por padrão.
                        if (data.status === "G") {
                            // poderia recalcular/atualizar trial aqui se o evento trouxer contador
                            // se não trouxer, deixa como está
                        }
                        if (["P", "C"].includes(data.status)) {
                            setRestantes(0);
                        }
                        // Em 'A', mantenha o valor atual até o próximo fetch/atualização
                    }
                }
            } catch { }
        };

        eventSource.onerror = () => {
            // Desconecta em caso de erro de rede
            eventSource.close();
        };

        eventSourceRef.current = eventSource;

        // Cleanup ao desmontar
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [clienteId]);

    // Incrementa contagem gratuita e atualiza UI local
    const incrementaTrial = async (userId) => {
        if (!userId) return;
        const res = await api.post('/billing/usage/trial');
        // Atualiza os trials localmente
        if (res.data?.success || res.data?.status) {
            setRestantes((ant) => {
                const novo = Math.max(ant - 1, 0);
                if (novo === 0) setPlanoStatus("G0");
                return novo;
            });
        }
    };

    // Incrementa assinante (50/mês) e atualiza UI local com base no 'restantes' do backend
    const incrementaAssinante = async (userId) => {
        if (!userId) return { status: false };
        try {
            const res = await api.post('/billing/usage/subscriber');

            if (res.data?.success || res.data?.status) {
                if (typeof res.data.restantes === "number" || typeof res.data.remaining === "number") {
                    setRestantes(res.data.restantes ?? res.data.remaining); // usa valor oficial do servidor
                } else {
                    // fallback (não esperado)
                    setRestantes((ant) => Math.max(ant - 1, 0));
                }
            }
            return {
                ...res.data,
                status: res.data?.status ?? res.data?.success,
                restantes: res.data?.restantes ?? res.data?.remaining,
            };
        } catch {
            return { status: false, erro: "Falha ao incrementar." };
        }
    };

    // Para reset manual (ex: após assinatura)
    const resetPlano = () => {
        setPlanoStatus(null);
        setRestantes(0);
        setImagensRestantes(null);
        setClienteId(null);
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }
    };

    return (
        <PlanContext.Provider value={{
            planoStatus,
            setPlanoStatus,
            restantes,
            imgUtilizadas,
            imagensRestantes,
            setRestantes,
            loadingPlano,
            fetchStatusPlano,
            incrementaTrial,
            incrementaAssinante,
            resetPlano,
            planoTipo,
            getQuotaAtual: () => getQuotaByPlan(planoTipo)
        }}>
            {children}
        </PlanContext.Provider>
    );
}

export function usePlano() {
    return useContext(PlanContext);
}
